import { ChevronDownIcon, ChevronUpIcon } from '@digital-retail/design-system-icons';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { withLabels } from '../../../../../utils/LabelsContext';
import { AccordionItemStyles } from './style';

const AccordionItem = ({
  title,
  children,
  isOpen,
  onToggle,
  variant,
  maxContentHeight = 400,
  labels,
  alwaysOpen = false,
}) => {
  const [isContentExpanded, setIsContentExpanded] = useState(false);
  const [shouldShowButton, setShouldShowButton] = useState(false);
  const contentRef = useRef(null);
  const { SHOW_LESS = 'Mostrar menos', SHOW_MORE = 'Mostrar más' } = labels || {};

  const baseClass = 'accordion__item';
  const itemClass = `${baseClass} ${baseClass}--${variant} ${isOpen ? `${baseClass}--open` : ''}`;
  const contentClass = `accordion__content ${isOpen ? 'accordion__content--open' : ''}`;

  // useEffect(() => {
  //   if (isOpen && contentRef.current) {
  //     const contentHeight = contentRef.current.scrollHeight;
  //     setShouldShowButton(contentHeight > maxContentHeight);
  //   }
  // }, [isOpen, children, maxContentHeight]);

  useEffect(() => {
    if (isOpen && contentRef.current) {
      let timerId;
      let retryCount = 0;
      const maxRetries = 10; // Retry up to 10 times for dynamic content (like Flix)

      const checkContentHeight = () => {
        if (contentRef.current) {
          const contentHeight = contentRef.current.scrollHeight;
          const hasContent = contentHeight > maxContentHeight;

          // If we found content or reached max retries, set the button state
          if (hasContent || retryCount >= maxRetries) {
            setShouldShowButton(hasContent);
          } else {
            // Retry with increasing delays for dynamic content loading
            retryCount += 1;
            timerId = setTimeout(checkContentHeight, 100 * retryCount);
          }
        }
      };

      const frameId = requestAnimationFrame(() => {
        timerId = setTimeout(checkContentHeight, 50);
      });

      return () => {
        cancelAnimationFrame(frameId);
        if (timerId) clearTimeout(timerId);
      };
    }
    return undefined;
  }, [isOpen, children, maxContentHeight]);
  const handleShowMoreToggle = () => {
    setIsContentExpanded(!isContentExpanded);
  };

  return (
    <div className={itemClass}>
      <button
        type="button"
        className="accordion__header"
        onClick={alwaysOpen ? undefined : onToggle}
        aria-expanded={isOpen}
        style={{ cursor: alwaysOpen ? 'default' : 'pointer' }}
      >
        <div className="accordion__title">{title}</div>
        {!alwaysOpen && (
          <div className="accordion__icon">
            {isOpen ? (
              <ChevronUpIcon boxSize="20px" color="gray.500" />
            ) : (
              <ChevronDownIcon boxSize="20px" color="gray.500" />
            )}
          </div>
        )}
      </button>

      <div className={contentClass}>
        <div
          className={`accordion__content-inner ${
            shouldShowButton && !isContentExpanded ? 'accordion__content-inner--truncated' : ''
          }`}
          style={{
            maxHeight: shouldShowButton && !isContentExpanded ? `${maxContentHeight}px` : 'none',
            overflow: shouldShowButton && !isContentExpanded ? 'hidden' : 'visible',
          }}
        >
          <div ref={contentRef}>{children}</div>
          {shouldShowButton && !isContentExpanded && <div className="accordion__content-gradient" />}
        </div>
        {shouldShowButton && (
          <button type="button" className="accordion__show-more-btn" onClick={handleShowMoreToggle}>
            {isContentExpanded ? SHOW_LESS : SHOW_MORE}
          </button>
        )}
      </div>

      <style jsx>{AccordionItemStyles}</style>
    </div>
  );
};

AccordionItem.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  children: PropTypes.node.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  variant: PropTypes.string,
  maxContentHeight: PropTypes.number,
  labels: PropTypes.object.isRequired,
  alwaysOpen: PropTypes.bool,
};

AccordionItem.defaultProps = {
  variant: 'default',
  maxContentHeight: 400,
  alwaysOpen: false,
};

export default withLabels(AccordionItem);
